import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';

const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Início', href: '#hero' },
    { name: 'Quem Somos', href: '#about' },
    { name: 'Galeria', href: '#gallery' },
    { name: 'Orçamento', href: '#contact' },
    { name: 'Kosher', href: '#kosher' },
  ];

  const scrollToSection = (href: string) => {
    // Fechar o menu primeiro
    setIsOpen(false);
    
    // Aguardar um pouco para o menu fechar antes de fazer o scroll
    setTimeout(() => {
      const element = document.querySelector(href) as HTMLElement;
      if (element) {
        // Calcular offset para compensar a altura da navbar
        const navbarHeight = window.innerWidth < 768 ? 70 : 80;
        const elementPosition = element.offsetTop - navbarHeight;
        
        // Usar scrollIntoView como fallback se window.scrollTo não funcionar
        try {
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
        } catch (error) {
          // Fallback para navegadores que não suportam window.scrollTo
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    }, 150); // Aumentar o delay para garantir que o menu feche
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-out ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 sm:h-18 md:h-20">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center"
          >
            <div>
              <h1 className="text-xl sm:text-2xl font-playfair font-bold text-gray-900">
                IMPETUS
              </h1>
              <p className="text-xs sm:text-sm text-gray-700 font-dancing text-base sm:text-lg">
                Gastronomia
              </p>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navItems.map((item, index) => (
              <motion.button
                key={item.name}
                onClick={() => scrollToSection(item.href)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="text-gray-700 hover:text-primary-600 font-medium transition-all duration-500 ease-out text-sm xl:text-base"
              >
                {item.name}
              </motion.button>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <motion.a
              href="https://api.whatsapp.com/send?phone=551132979158&text=Olá%20vim%20pelo%20site,%20preciso%20de%20um%20orçamento!"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary flex items-center space-x-2 text-sm xl:text-base px-4 xl:px-6 py-2 xl:py-3"
            >
              <Phone className="w-4 h-4" />
              <span className="xl:hidden">Orçamento</span>
              <span className="hidden xl:inline">Solicitar Orçamento</span>
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-all duration-500 ease-out"
          >
            {isOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="lg:hidden bg-white/95 backdrop-blur-md border-t border-gray-200"
          >
            <div className="container-custom py-4 sm:py-6">
              <div className="flex flex-col space-y-3 sm:space-y-4">
                {navItems.map((item, index) => (
                  <motion.button
                    key={item.name}
                    onClick={() => scrollToSection(item.href)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-left text-gray-700 hover:text-primary-600 font-medium py-2 sm:py-3 transition-all duration-500 ease-out text-base sm:text-lg"
                  >
                    {item.name}
                  </motion.button>
                ))}
                <motion.a
                  href="https://api.whatsapp.com/send?phone=551132979158&text=Olá%20vim%20pelo%20site,%20preciso%20de%20um%20orçamento!"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navItems.length * 0.1 }}
                  className="btn-primary flex items-center justify-center space-x-2 mt-4 text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
                >
                  <Phone className="w-4 h-4" />
                  <span>Orçamento</span>
                </motion.a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navigation;
